import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Row,
  Col,
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";

import StatusButton from "components/StatusButton";
import FileUpload from "../../../components/Input/fileUpload";
import ProposalPanel from "./components/ProposalPanel";
import "./components/cotacaoEmLote.css";
import "../GoBID/components/simulationModal.css";

import api from "../../../services/api";
import ApiHubBackend from "../../../services/ApiHubBackend";
import { useCompanies } from "hooks/companies";
import { useToast } from "../../../hooks/toast";
import fileDownload from "js-file-download";
import { formatCurrency } from "../../../utils/formatCurrency";
import { formatDate } from "../../../utils/formatDate";
import SpecificEmpresaRequiredNotice from "../../../components/SpecificEmpresaRequiredNotice";
import {
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotDescriptionStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotTitleStyle,
} from "../../../components/autopilot/styles";
import { propostaEscolhidaDe } from "../ListagemCotacoes/agregarPropostas"; // PO1-2570

const urlPostImportCsv = "/embarcadores/embarques/import-csv";
const urlGetModelo = "/embarcadores/embarques/modelo";
const urlGetModeloCsv = "/embarcadores/embarques/modelo-csv";
const urlGetSimulaFrete = "/frete/consulta-frete";

const TABLE_PAGE_SIZE = 10;

const customTotal = (from, to, size) => (
  <span className="react-bootstrap-table-pagination-total">
    Mostrando {from} a {to} de {size} resultados
  </span>
);

const paginationOptions = {
  sizePerPage: TABLE_PAGE_SIZE,
  pageStartIndex: 1,
  alwaysShowAllBtns: true,
  withFirstAndLast: true,
  hideSizePerPage: true,
  firstPageText: "Primeira",
  prePageText: "Voltar",
  nextPageText: "Próximo",
  lastPageText: "Última",
  showTotal: true,
  paginationTotalRenderer: customTotal,
};

const heroShellStyle = {
  ...autopilotCardStyle,
  display: "flex",
  flexDirection: "column",
  background: "var(--everlog-card-hero-bg)",
};

const SectionShell = ({
  className = "",
  eyebrow,
  title,
  description,
  actions = null,
  children,
}) => (
  <Card className={`card-everlog-shell ${className}`.trim()} style={heroShellStyle}>
    <CardHeader
      className={`cotacao-lote-shell__header${actions ? " cotacao-lote-shell__header--split" : ""}`}
      style={autopilotHeaderStyle}
    >
      <div>
        {eyebrow ? <div style={autopilotEyebrowStyle}>{eyebrow}</div> : null}
        {title ? <div style={autopilotTitleStyle}>{title}</div> : null}
        {description ? <p style={autopilotDescriptionStyle}>{description}</p> : null}
      </div>
      {actions ? <div className="cotacao-lote-shell__actions">{actions}</div> : null}
    </CardHeader>
    <CardBody className="cotacao-lote-shell__body" style={autopilotBodyStyle}>
      {children}
    </CardBody>
  </Card>
);

const exportToCsv = (embarques, simulations) => {
  const header = ["Embarque", "Cliente", "Cidade", "UF", "Transportadora", "Valor Frete", "Tipo Frete"];
  const rows = embarques.map((emb) => {
    const transportadora = emb.transportadoraEscolhida || emb.transportadora || "";
    const sims = simulations[emb._id] || [];
    const chosen = sims.find((s) => s.transportadora === transportadora);
    const valorFrete = chosen ? formatCurrency(chosen.valor) : "";
    return [
      emb.numEmbarque,
      emb.cliente,
      emb.cidadeDestino,
      emb.ufDestino,
      transportadora,
      valorFrete,
      emb.tipoFrete,
    ];
  });

  const csvContent = [header, ...rows]
    .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");

  fileDownload("\uFEFF" + csvContent, `cotacao-lote-${new Date().toISOString().split("T")[0]}.csv`, "text/csv;charset=utf-8");
};

const CotacaoEmLote = () => {
  const { empresa, hasEmpresaSelection } = useCompanies();
  const empresaId = empresa?.value;
  const { addToast } = useToast();
  const [embarques, setEmbarques] = useState([]);
  const [filteredEmbarques, setFilteredEmbarques] = useState([]);
  const [simulations, setSimulations] = useState({});
  // Indicadores ANTT (piso mínimo, por proposta) + Carbono (por embarque),
  // calculados no backend_hub após a simulação. Chaveado por embarque._id.
  const [indicadores, setIndicadores] = useState({});

  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [activeEmbarque, setActiveEmbarque] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const [filterCidade, setFilterCidade] = useState("");
  const [filterDestinatario, setFilterDestinatario] = useState("");
  const [filterTransportadora, setFilterTransportadora] = useState("");
  const [searchText, setSearchText] = useState("");
  const [filterDataInicio, setFilterDataInicio] = useState("");
  const [filterDataFim, setFilterDataFim] = useState("");

  const [loadingEmbarques, setLoadingEmbarques] = useState(false);
  const [pendentesDays, setPendentesDays] = useState(7);

  const [simulating, setSimulating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [applyingCheapest, setApplyingCheapest] = useState(false);
  const [applyingFastest, setApplyingFastest] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  // Bloqueia ações enquanto qualquer seleção em lote (mais barata/mais rápida)
  // está sendo persistida.
  const aplicandoSelecao = applyingCheapest || applyingFastest;

  // Avisa o usuário se ele tentar sair da página durante uma gravação em curso.
  useEffect(() => {
    if (!aplicandoSelecao) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "Há uma seleção sendo salva. Aguarde antes de sair.";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [aplicandoSelecao]);

  // Apply filters whenever embarques or filter values change
  useEffect(() => {
    let filtered = embarques;

    if (filterCidade) {
      filtered = filtered.filter((e) =>
        (e.cidadeDestino || "").toLowerCase().includes(filterCidade.toLowerCase())
      );
    }
    if (filterDestinatario) {
      filtered = filtered.filter((e) =>
        (e.cliente || "").toLowerCase().includes(filterDestinatario.toLowerCase())
      );
    }
    if (filterTransportadora) {
      const t = filterTransportadora.toLowerCase();
      filtered = filtered.filter((e) =>
        (e.transportadoraEscolhida || e.transportadora || "").toLowerCase().includes(t)
      );
    }
    if (searchText) {
      filtered = filtered.filter((e) =>
        String(e.numEmbarque || "").includes(searchText)
      );
    }
    if (filterDataInicio) {
      // Input type=date entrega "YYYY-MM-DD". Comparamos contra createdAt
      // (timestamp original do Mongo). Inclui o dia inteiro do início.
      const inicio = new Date(`${filterDataInicio}T00:00:00`);
      filtered = filtered.filter((e) => {
        const ref = e.createdAt ? new Date(e.createdAt) : null;
        return ref && !Number.isNaN(ref.getTime()) && ref >= inicio;
      });
    }
    if (filterDataFim) {
      const fim = new Date(`${filterDataFim}T23:59:59.999`);
      filtered = filtered.filter((e) => {
        const ref = e.createdAt ? new Date(e.createdAt) : null;
        return ref && !Number.isNaN(ref.getTime()) && ref <= fim;
      });
    }

    setFilteredEmbarques(filtered);
  }, [embarques, filterCidade, filterDestinatario, filterTransportadora, searchText, filterDataInicio, filterDataFim]);

  // Carregamento sob demanda: o caso primário desta tela é importar + simular
  // do zero. Carregar pendentes históricos é exceção, então fica em um botão
  // explícito com seletor de período (não roda automaticamente ao abrir).
  const handleCarregarPendentes = useCallback(async () => {
    if (!empresaId) return;
    setLoadingEmbarques(true);
    try {
      const r = await api.get(
        `/embarcadores/embarques/${empresaId}?status=Pendente&days=${pendentesDays}`
      );
      const rows = Array.isArray(r.data) ? r.data : [];
      const mapped = rows
        .sort(
          (a, b) =>
            new Date(b.createdAt || b.dataEmbarque || 0) -
            new Date(a.createdAt || a.dataEmbarque || 0)
        )
        .map((emb) => ({
          ...emb,
          dataEmbarque: formatDate(emb.dataEmbarque),
          valorNF: formatCurrency(emb.valorNF),
          statusEmbarque: emb.statusEmbarque || "Importado",
          transportadoraEscolhida: emb.transportadora || "",
        }));
      // Mescla com o que já está em memória (vindo de imports), evitando
      // duplicatas pelo _id.
      setEmbarques((prev) => {
        const existentes = new Set(prev.map((e) => String(e._id)));
        const novos = mapped.filter((e) => !existentes.has(String(e._id)));
        return [...prev, ...novos];
      });
      addToast({
        place: "tr",
        type: "success",
        title: "Pendentes carregados",
        description:
          rows.length > 0
            ? `${rows.length} embarque(s) pendentes dos últimos ${pendentesDays} dias.`
            : `Nenhum pendente nos últimos ${pendentesDays} dias.`,
        icon: "tim-icons icon-check-2",
      });
    } catch {
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao carregar pendentes",
        description: "Tente novamente em alguns segundos.",
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setLoadingEmbarques(false);
    }
  }, [empresaId, pendentesDays, addToast]);

  useEffect(() => {
    if (hasEmpresaSelection) {
      return;
    }

    setEmbarques([]);
    setFilteredEmbarques([]);
    setSimulations({});
    setSelectedRowIds([]);
    setActiveEmbarque(null);
    setIsPanelOpen(false);
    setIsImportModalOpen(false);
    setImporting(false);
    setSimulating(false);
  }, [hasEmpresaSelection]);

  const handleOpenImportModal = useCallback(() => {
    setIsImportModalOpen(true);
  }, []);

  // Modelo da planilha, no mesmo padrão do catálogo de transportadoras. Aqui o
  // arquivo é CSV, não xlsx: a importação desta tela só aceita CSV, e entregar
  // xlsx obrigaria o cliente a converter antes de subir.
  // Modelo da planilha, no mesmo padrão do catálogo de transportadoras.
  // Padrão é .xlsx, que é o formato em que o cliente trabalha; o CSV fica como
  // alternativa para quem prefere ou automatiza. A importação aceita os dois.
  const baixarModelo = useCallback(
    async (formato = "xlsx") => {
      const ehCsv = formato === "csv";
      try {
        const { data } = await api.get(ehCsv ? urlGetModeloCsv : urlGetModelo, {
          responseType: "blob",
        });
        fileDownload(
          data,
          ehCsv ? "modelo-cotacao-em-lote.csv" : "modelo-cotacao-em-lote.xlsx",
          ehCsv
            ? "text/csv;charset=utf-8"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        );
      } catch (e) {
        addToast({
          place: "tr",
          type: "danger",
          title: "Erro",
          description: "Falha ao baixar o modelo.",
        });
      }
    },
    [addToast]
  );

  const handleImportSuccess = useCallback((result) => {
    setIsImportModalOpen(false);
    setImporting(true);
    const imported = Array.isArray(result?.data?.embarques) ? result.data.embarques : [];
    const warnings = Array.isArray(result?.data?.warnings) ? result.data.warnings : [];

    if (imported.length > 0) {
      const mapped = imported.map((emb) => ({
        ...emb,
        dataEmbarque: formatDate(emb.dataEmbarque),
        valorNF: formatCurrency(emb.valorNF),
        statusEmbarque: emb.statusEmbarque || "Importado",
        transportadoraEscolhida: emb.transportadora || "",
      }));
      setEmbarques((prev) => [...prev, ...mapped]);
    }

    if (warnings.length > 0 && imported.length === 0) {
      addToast({
        place: "tr",
        type: "warning",
        title: "Nada novo importado",
        description: `${warnings.length} embarque(s) já existem na base e foram ignorados.`,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } else if (warnings.length > 0) {
      addToast({
        place: "tr",
        type: "warning",
        title: "Importação com avisos",
        description: `${imported.length} novo(s) importado(s); ${warnings.length} já existente(s) ignorado(s).`,
        icon: "tim-icons icon-alert-circle-exc",
      });
    }

    setImporting(false);
  }, [addToast]);

  // Busca os indicadores ANTT/Carbono no backend_hub (POST /cotacao/indicadores)
  // DEPOIS que as cotações já apareceram — complementar, em paralelo, sem
  // bloquear a tela. Falha aqui não quebra a cotação (badges só não aparecem).
  // A origem (origem→destino p/ distância) é resolvida no backend pelo cadastro
  // do embarcador; o front só envia destino, peso e as propostas.
  const fetchIndicadores = useCallback(
    async (embarquesReq) => {
      if (!empresaId || !Array.isArray(embarquesReq) || embarquesReq.length === 0) {
        return;
      }
      try {
        const { data } = await api.post("/cotacao/indicadores", {
          idEmbarcador: empresaId,
          embarques: embarquesReq,
        });
        const byRef = {};
        (data && data.embarques ? data.embarques : []).forEach((e) => {
          if (e && e.ref != null) byRef[e.ref] = e;
        });
        setIndicadores((prev) => ({ ...prev, ...byRef }));
      } catch (err) {
        console.error("Erro ao calcular indicadores ANTT/carbono:", err);
      }
    },
    [empresaId]
  );

  const handleSimularFretes = useCallback(async () => {
    if (!empresaId) return;

    // Opera apenas sobre o conjunto VISÍVEL na tela (filteredEmbarques).
    // Evita simular embarques antigos que não estão no filtro/seleção atual.
    const toSimulate =
      selectedRowIds.length > 0
        ? filteredEmbarques.filter((e) => selectedRowIds.includes(e._id))
        : filteredEmbarques;

    if (toSimulate.length === 0) return;

    setSimulating(true);
    try {
      const payload = {
        credencial: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
        codigoModal: 1,
        quantidadeRetornos: 5,
        idEmbarcador: empresaId,
        idEmbarcadorRemetente: "",
        embarques: toSimulate.map((e) => ({
          numero: e.numEmbarque,
          data: e.dataEmbarque,
          CNPJEmitente: e.cnpjRemetente,
          CEPDestinatario: e.cepDestino,
          // PO1-2903/2904: o que cuba é o volume em m³. A planilha antiga traz
          // esse valor na coluna Densidade, e o backend segue aceitando esse
          // nome como apelido; a planilha nova usa a coluna Volume, e sem
          // mandá-lo aqui a carga importada por ela não seria cubada.
          volume: e.volume != null && e.volume !== "" ? e.volume : e.densidade,
          densidade: e.densidade,
          altura: e.altura,
          largura: e.largura,
          comprimento: e.comprimento,
          peso: e.pesoBruto,
          valor: e.valorNF,
        })),
      };

      const response = await ApiHubBackend.post(urlGetSimulaFrete, payload);
      const simulatedData = response.data;

      const newSimulations = { ...simulations };
      const indicadoresReq = [];
      const updatedEmbarques = embarques.map((emb) => {
        const result = simulatedData.find(
          (s) => s != null && String(s.numero) === String(emb.numEmbarque)
        );
        if (!result || !Array.isArray(result.freteCalculado)) return emb;

        // PO1-3178: a `fonte` deixou de ser descartada aqui. Sem ela a tela mostrava
        // a mesma transportadora duas vezes (tabela e api) sem dizer qual era qual.
        const proposals = result.freteCalculado.map((f) => ({
          transportadora: f.transportadora,
          idTransportadora: f.idTransportadora ?? null,
          valor: f.custoFrete != null ? Number(f.custoFrete) : 0,
          prazo: f.prazo ?? null,
          fonte: f.fonte ?? null,
          // PO1-2570 (decisão 5A): identificação da proposta na API da transportadora.
          conectorKey: f.conectorKey ?? null,
          servico: f.servico != null ? String(f.servico) : null,
          protocolo: f.protocolo != null ? String(f.protocolo) : null,
        }));

        // PO1-3200: spots lançados à mão sobrevivem a uma nova simulação.
        const spotsAtuais = (simulations[emb._id] || []).filter((p) => p.fonte === "spot");
        newSimulations[emb._id] = [...proposals, ...spotsAtuais];

        // Coleta p/ indicadores ANTT/Carbono. A origem é resolvida no backend
        // pelo cadastro do embarcador; result.cidadeDestino = Id da cidade
        // destino resolvido pelo consulta-frete.
        indicadoresReq.push({
          ref: emb._id,
          destino: {
            idCidade: result.cidadeDestino != null ? Number(result.cidadeDestino) : null,
          },
          pesoKg: Number(emb.pesoBruto) || 0,
          // PO1-3178: UMA entrada por transportadora. O backend já deduplica
          // tabela x api, mas um conector pode devolver vários serviços da mesma
          // transportadora (Correios PAC e SEDEX), e aí o ANTT/carbono contaria a
          // transportadora duas vezes. Fica a mais barata, que é a que o
          // "Selecionar mais barato" escolheria.
          propostas: Array.from(
            proposals
              .filter((p) => p.idTransportadora != null)
              .reduce((mapa, p) => {
                const atual = mapa.get(p.idTransportadora);
                if (!atual || p.valor < atual.valorFrete) {
                  mapa.set(p.idTransportadora, {
                    idTransportadora: p.idTransportadora,
                    valorFrete: p.valor,
                  });
                }
                return mapa;
              }, new Map())
              .values()
          ),
        });

        const melhorFrete = proposals.length > 0
          ? proposals.reduce((min, p) => (p.valor < min.valor ? p : min), proposals[0])
          : null;

        return {
          ...emb,
          statusEmbarque: "Simulado",
          melhorFrete: melhorFrete ? melhorFrete.valor : null,
        };
      });

      setSimulations(newSimulations);
      setEmbarques(updatedEmbarques);

      // Indicadores ANTT/Carbono em paralelo — não bloqueia a exibição do frete.
      fetchIndicadores(indicadoresReq);

      // Sync with active panel if open
      if (activeEmbarque) {
        const updated = updatedEmbarques.find((e) => e._id === activeEmbarque._id);
        if (updated) setActiveEmbarque(updated);
      }
    } catch (err) {
      console.error("Erro ao simular fretes:", err);
      alert("Erro ao simular fretes. Verifique o console.");
    } finally {
      setSimulating(false);
    }
  }, [empresaId, filteredEmbarques, selectedRowIds, simulations, embarques, activeEmbarque, fetchIndicadores]);

  // Aplica em lote a transportadora escolhida por um critério (mais barata,
  // mais rápida, ...). `escolher(proposals)` retorna a proposta vencedora, ou
  // null p/ pular o embarque (ex.: nenhuma proposta com prazo). Persiste em uma
  // única chamada bulk (sequencial no servidor) p/ não estourar o pool do Mongo.
  // Atua só sobre o que está visível (filteredEmbarques): respeita os filtros.
  const aplicarSelecaoEmLote = useCallback(
    async (escolher, descricaoSucesso, setLoading) => {
      const updates = filteredEmbarques
        .filter((emb) => simulations[emb._id]?.length > 0)
        .map((emb) => {
          const proposals = simulations[emb._id];
          const escolhida = escolher(proposals);
          if (!escolhida) return null;
          return {
            id: emb._id,
            transportadora: escolhida.transportadora,
            transportadoraId: escolhida.idTransportadora ?? null,
            propostaEscolhida: propostaEscolhidaDe(escolhida), // PO1-2570
            // PO1-2473: persiste as propostas simuladas p/ aparecerem na Listagem.
            cotacoes: proposals,
          };
        })
        .filter(Boolean);

      if (updates.length === 0) return;

      setEmbarques((prev) =>
        prev.map((emb) => {
          const update = updates.find((u) => u.id === emb._id);
          return update ? { ...emb, transportadoraEscolhida: update.transportadora } : emb;
        })
      );

      setLoading(true);
      try {
        const payload = {
          updates: updates.map(({ id, transportadora, transportadoraId, propostaEscolhida, cotacoes }) => ({
            id,
            transportadora,
            transportadoraId,
            propostaEscolhida,
            statusEmbarque: "Simulado",
            cotacoes,
          })),
        };
        const { data } = await api.put(
          "/embarcadores/embarques/bulk-update",
          payload
        );
        const okCount = data?.updatedCount ?? 0;
        const failCount = data?.failedCount ?? 0;
        // PO1-2713: itens barrados pela alçada (acima do orçado) ficam em
        // Stand-by aguardando o aprovador. Contam como salvos no updatedCount.
        const pendentes = Array.isArray(data?.pendentesAprovacao)
          ? data.pendentesAprovacao
          : [];
        if (pendentes.length > 0) {
          addToast({
            place: "tr",
            type: "warning",
            title: "Enviadas para aprovação",
            description: `${pendentes.length} cotação(ões) enviada(s) para aprovação por estar(em) acima do orçado.`,
            icon: "tim-icons icon-alert-circle-exc",
          });
        }
        if (failCount === 0) {
          addToast({
            place: "tr",
            type: "success",
            title: "Seleção aplicada",
            description: descricaoSucesso(okCount),
            icon: "tim-icons icon-check-2",
          });
        } else {
          addToast({
            place: "tr",
            type: "warning",
            title: "Seleção parcialmente aplicada",
            description: `${okCount} salvos, ${failCount} falharam. Tente novamente os pendentes.`,
            icon: "tim-icons icon-alert-circle-exc",
          });
        }
      } catch {
        addToast({
          place: "tr",
          type: "danger",
          title: "Erro ao salvar",
          description: "Não foi possível persistir as seleções. Tente novamente.",
          icon: "tim-icons icon-alert-circle-exc",
        });
      } finally {
        setLoading(false);
      }
    },
    [filteredEmbarques, simulations, addToast]
  );

  const handleSelecionarMaisBarato = useCallback(
    () =>
      aplicarSelecaoEmLote(
        (proposals) =>
          proposals.reduce((min, p) => (p.valor < min.valor ? p : min), proposals[0]),
        (n) => `Transportadora mais barata salva em ${n} embarque(s).`,
        setApplyingCheapest
      ),
    [aplicarSelecaoEmLote]
  );

  const handleSelecionarMaisRapido = useCallback(
    () =>
      aplicarSelecaoEmLote(
        // Menor prazo entre as propostas que TÊM prazo; empate de prazo →
        // a mais barata. Embarque sem nenhum prazo é pulado (retorna null).
        (proposals) => {
          const comPrazo = proposals.filter((p) => p.prazo != null);
          if (comPrazo.length === 0) return null;
          return comPrazo.reduce(
            (best, p) =>
              p.prazo < best.prazo || (p.prazo === best.prazo && p.valor < best.valor)
                ? p
                : best,
            comPrazo[0]
          );
        },
        (n) => `Transportadora mais rápida salva em ${n} embarque(s).`,
        setApplyingFastest
      ),
    [aplicarSelecaoEmLote]
  );

  // PO1-3200: "+ Frete Spot" no painel. Um spot por transportadora (incluir de
  // novo substitui o valor). Fica em `simulations`, junto das propostas
  // simuladas, e é gravado em `cotacoes` quando a seleção é aplicada.
  const mesmoSpot = (a, b) =>
    a.fonte === "spot" && b.fonte === "spot" && a.idTransportadora === b.idTransportadora;

  const handleAddSpot = useCallback((id, proposta) => {
    setSimulations((prev) => ({
      ...prev,
      [id]: [...(prev[id] || []).filter((p) => !mesmoSpot(p, proposta)), proposta],
    }));
  }, []);

  const handleRemoveSpot = useCallback((id, proposta) => {
    setSimulations((prev) => ({
      ...prev,
      [id]: (prev[id] || []).filter((p) => !mesmoSpot(p, proposta)),
    }));
  }, []);

  const handleApplyTransportadora = useCallback(async (id, transportadora, proposta) => {
    // Atualiza state local imediatamente para feedback visual instantâneo
    setEmbarques((prev) =>
      prev.map((emb) =>
        emb._id === id ? { ...emb, transportadoraEscolhida: transportadora } : emb
      )
    );
    if (activeEmbarque?._id === id) {
      setActiveEmbarque((prev) => ({ ...prev, transportadoraEscolhida: transportadora }));
    }

    // PO1-2473: propostas simuladas deste embarque, p/ persistir junto e
    // aparecerem na Listagem de Cotações.
    const proposals = simulations[id] || [];
    // PO1-3200: o painel manda a proposta escolhida; o nome sozinho não
    // distingue tabela de spot da mesma transportadora.
    const chosen = proposta || proposals.find((p) => p.transportadora === transportadora);

    // Persiste no backend
    try {
      const payload = {
        transportadora,
        transportadoraId: chosen ? chosen.idTransportadora ?? null : null,
        propostaEscolhida: chosen ? propostaEscolhidaDe(chosen) : null, // PO1-2570
        statusEmbarque: "Simulado",
      };
      if (proposals.length > 0) payload.cotacoes = proposals;
      await api.put(`/embarcadores/embarques/${id}`, payload);
      addToast({
        place: "tr",
        type: "success",
        title: "Seleção aplicada",
        description: `Transportadora "${transportadora}" salva com sucesso.`,
        icon: "tim-icons icon-check-2",
      });
    } catch {
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao salvar",
        description: "Não foi possível persistir a seleção. Tente novamente.",
        icon: "tim-icons icon-alert-circle-exc",
      });
    }
  }, [activeEmbarque, addToast, simulations]);

  const handleOpenFullQuote = useCallback(() => {
    setIsPanelOpen(false);
  }, []);

  const handleExportar = useCallback(() => {
    if (embarques.length === 0) {
      alert("Nenhum embarque para exportar.");
      return;
    }
    exportToCsv(embarques, simulations);
  }, [embarques, simulations]);

  const selectRow = useMemo(() => ({
    mode: "checkbox",
    selected: selectedRowIds,
    onSelect: (row, isSelected) => {
      setSelectedRowIds((prev) =>
        isSelected ? [...prev, row._id] : prev.filter((id) => id !== row._id)
      );
    },
    onSelectAll: (isSelected, rows) => {
      setSelectedRowIds(isSelected ? rows.map((r) => r._id) : []);
    },
  }), [selectedRowIds]);

  const rowEvents = useMemo(() => ({
    onClick: (e, row) => {
      if (!row) return;
      const target = e?.target;
      if (target && typeof target.closest === "function") {
        if (target.closest(".selection-cell, .selection-cell-header")) return;
      }
      setActiveEmbarque(row);
      setIsPanelOpen(true);
    },
  }), []);

  const columns = useMemo(() => [
    {
      dataField: "dataEmbarque",
      text: "Data",
    },
    {
      dataField: "numEmbarque",
      text: "Embarque",
    },
    {
      dataField: "cliente",
      text: "Destinatário",
    },
    {
      dataField: "cidadeDestino",
      text: "Cidade",
    },
    {
      dataField: "ufDestino",
      text: "UF",
    },
    {
      dataField: "pesoBruto",
      text: "KG",
    },
    {
      dataField: "densidade",
      text: "M3",
    },
    {
      dataField: "valorNF",
      text: "Valor NF",
    },
    {
      dataField: "melhorFrete",
      text: "Melhor Frete",
      formatter: (cell) =>
        cell != null ? (
          <span className="badge-frete">{formatCurrency(cell)}</span>
        ) : (
          <span style={{ color: "var(--everlog-card-muted)", fontSize: 12 }}>—</span>
        ),
    },
    {
      // "Melhor Prazo": menor prazo (entrega mais rápida) entre TODAS as
      // transportadoras simuladas do embarque, independente do custo — análogo
      // ao "Melhor Frete" (menor custo). Basta UMA transportadora ter prazo no
      // destino para preencher. Deriva de `simulations`, por isso é dummy field
      // e o useMemo depende de `simulations`.
      dataField: "melhorPrazo",
      isDummyField: true,
      text: "Melhor Prazo",
      formatter: (cell, row) => {
        const muted = (
          <span style={{ color: "var(--everlog-card-muted)", fontSize: 12 }}>—</span>
        );
        const props = simulations[row._id];
        if (!props || props.length === 0) return muted;
        const prazos = props.map((p) => p.prazo).filter((p) => p != null);
        if (prazos.length === 0) return muted;
        return (
          <span className="badge-prazo">{`${Math.min(...prazos)}d`}</span>
        );
      },
    },
    {
      dataField: "transportadoraEscolhida",
      text: "Transportadora",
      formatter: (cell) =>
        cell ? (
          <span style={{ color: "var(--everlog-card-title)", fontSize: 12 }}>{cell}</span>
        ) : (
          <span style={{ color: "var(--everlog-card-muted)", fontSize: 12 }}>—</span>
        ),
    },
    {
      dataField: "statusEmbarque",
      text: "Status",
      formatter: (cell) => <StatusButton status={cell} />,
    },
  ], [simulations]);

  const activeFilterCount = useMemo(
    () =>
      [
        filterCidade,
        filterDestinatario,
        filterTransportadora,
        searchText,
        filterDataInicio,
        filterDataFim,
      ].filter(Boolean).length,
    [
      filterCidade,
      filterDestinatario,
      filterTransportadora,
      searchText,
      filterDataInicio,
      filterDataFim,
    ]
  );

  const hasActiveFilters = activeFilterCount > 0;
  const selectedCount = selectedRowIds.length;

  const handleClearFilters = useCallback(() => {
    setFilterCidade("");
    setFilterDestinatario("");
    setFilterTransportadora("");
    setSearchText("");
    setFilterDataInicio("");
    setFilterDataFim("");
  }, []);

  if (!hasEmpresaSelection) {
    return (
      <div className="content cotacao-lote-page">
        <SpecificEmpresaRequiredNotice />
      </div>
    );
  }

  const resultsDescription = importing
    ? "Atualizando os embarques importados."
    : loadingEmbarques
      ? "Carregando pendentes do banco..."
      : embarques.length === 0
        ? 'Importe embarques ou clique em "Carregar pendentes" para começar.'
        : `${filteredEmbarques.length} resultado(s) visíveis de ${embarques.length} embarque(s) no lote atual.`;

  return (
    <div className="content cotacao-lote-page">
      <Card className="card-everlog-shell cotacao-lote-hero" style={heroShellStyle}>
        <CardBody className="cotacao-lote-hero__body" style={autopilotBodyStyle}>
          <div className="cotacao-lote-hero__intro">
            <span className="cotacao-lote-hero__eyebrow">Negociação</span>
            <h2 className="cotacao-lote-hero__title">Cotação em lote</h2>
            <p className="cotacao-lote-hero__description">
              Importe embarques, simule múltiplos fretes e use o painel lateral para
              aplicar a melhor proposta sem sair da tabela.
            </p>
          </div>

          <div className="cotacao-lote-hero__actions">
            <Button
              className="btn-sm cotacao-lote-toolbar-btn cotacao-lote-toolbar-btn--primary"
              onClick={handleOpenImportModal}
              disabled={importing || aplicandoSelecao}
            >
              <i className="tim-icons icon-cloud-upload-94" />
              {importing ? " Carregando..." : " Importar embarques"}
            </Button>
            <Button
              className="btn-sm cotacao-lote-toolbar-btn cotacao-lote-toolbar-btn--neutral"
              onClick={handleSimularFretes}
              disabled={simulating || aplicandoSelecao || embarques.length === 0}
            >
              <i className="tim-icons icon-refresh-02" />
              {simulating ? " Simulando..." : " Simular fretes"}
              {selectedCount > 0 && ` (${selectedCount})`}
            </Button>
            <span className="cotacao-lote-toolbar-divider" aria-hidden="true" />
            <Button
              className="btn-sm cotacao-lote-toolbar-btn cotacao-lote-toolbar-btn--warning"
              onClick={handleSelecionarMaisBarato}
              disabled={aplicandoSelecao || Object.keys(simulations).length === 0}
            >
              <i className="tim-icons icon-trophy" />
              {applyingCheapest ? " Salvando seleção..." : " Selecionar mais barato"}
            </Button>
            <Button
              className="btn-sm cotacao-lote-toolbar-btn cotacao-lote-toolbar-btn--success"
              onClick={handleSelecionarMaisRapido}
              disabled={aplicandoSelecao || Object.keys(simulations).length === 0}
            >
              <i className="tim-icons icon-delivery-fast" />
              {applyingFastest ? " Salvando seleção..." : " Selecionar mais rápido"}
            </Button>
            <span className="cotacao-lote-toolbar-divider" aria-hidden="true" />
            <Button
              className="btn-sm cotacao-lote-toolbar-btn cotacao-lote-toolbar-btn--neutral"
              onClick={handleExportar}
              disabled={embarques.length === 0 || aplicandoSelecao}
            >
              <i className="tim-icons icon-cloud-download-93" /> Exportar resultado
            </Button>
          </div>
        </CardBody>
      </Card>

      <div className={`cotacao-lote-layout${isPanelOpen ? " with-panel" : ""}`}>
        <div className="cotacao-lote-table">
          <SectionShell
            className="cotacao-lote-results-shell"
            eyebrow="Operação em lote"
            title="Embarques importados"
            description={resultsDescription}
            actions={
              <div className="cotacao-lote-results__actions">
                {embarques.length > 0 && hasActiveFilters ? (
                  <button
                    type="button"
                    className="cotacao-lote-link-button"
                    onClick={handleClearFilters}
                  >
                    Limpar filtros
                  </button>
                ) : null}
                <Input
                  type="select"
                  value={pendentesDays}
                  disabled={loadingEmbarques || aplicandoSelecao}
                  onChange={(e) => setPendentesDays(Number(e.target.value))}
                  className="cotacao-lote-filter-input"
                  bsSize="sm"
                  style={{ width: 130 }}
                >
                  <option value={7}>Últimos 7 dias</option>
                  <option value={15}>Últimos 15 dias</option>
                  <option value={30}>Últimos 30 dias</option>
                  <option value={60}>Últimos 60 dias</option>
                </Input>
                <Button
                  className="btn-sm cotacao-lote-toolbar-btn cotacao-lote-toolbar-btn--neutral"
                  onClick={handleCarregarPendentes}
                  disabled={loadingEmbarques || aplicandoSelecao}
                >
                  <i className="tim-icons icon-bullet-list-67" />
                  {loadingEmbarques ? " Carregando..." : " Carregar pendentes"}
                </Button>
              </div>
            }
          >
            {embarques.length > 0 && (
              <div className="cotacao-lote-filters-panel cotacao-lote-filters-panel--inline">
                <Row>
                  <Col lg="2" md="6" className="mb-3 mb-lg-2">
                    <label className="cotacao-lote-filter-label">Data De</label>
                    <Input
                      type="date"
                      value={filterDataInicio}
                      onChange={(e) => setFilterDataInicio(e.target.value)}
                      className="cotacao-lote-filter-input"
                      bsSize="sm"
                    />
                  </Col>
                  <Col lg="2" md="6" className="mb-3 mb-lg-2">
                    <label className="cotacao-lote-filter-label">Data Até</label>
                    <Input
                      type="date"
                      value={filterDataFim}
                      onChange={(e) => setFilterDataFim(e.target.value)}
                      className="cotacao-lote-filter-input"
                      bsSize="sm"
                    />
                  </Col>
                  <Col lg="3" md="6" className="mb-3 mb-lg-2">
                    <label className="cotacao-lote-filter-label">Cidade</label>
                    <Input
                      placeholder="Cidade"
                      value={filterCidade}
                      onChange={(e) => setFilterCidade(e.target.value)}
                      className="cotacao-lote-filter-input"
                      bsSize="sm"
                    />
                  </Col>
                  <Col lg="3" md="6" className="mb-3 mb-lg-2">
                    <label className="cotacao-lote-filter-label">Nº Embarque</label>
                    <Input
                      placeholder="Nº Embarque"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      className="cotacao-lote-filter-input"
                      bsSize="sm"
                    />
                  </Col>
                </Row>
                <Row>
                  <Col lg="6" md="6" className="mb-3 mb-lg-0">
                    <label className="cotacao-lote-filter-label">Destinatário</label>
                    <Input
                      placeholder="Destinatário"
                      value={filterDestinatario}
                      onChange={(e) => setFilterDestinatario(e.target.value)}
                      className="cotacao-lote-filter-input"
                      bsSize="sm"
                    />
                  </Col>
                  <Col lg="6" md="6" className="mb-0">
                    <label className="cotacao-lote-filter-label">Transportadora</label>
                    <Input
                      placeholder="Transportadora"
                      value={filterTransportadora}
                      onChange={(e) => setFilterTransportadora(e.target.value)}
                      className="cotacao-lote-filter-input"
                      bsSize="sm"
                    />
                  </Col>
                </Row>
              </div>
            )}

            {simulating && (
              <div className="cotacao-lote-progress-panel">
                <div
                  className="spinner-border text-info"
                  role="status"
                  style={{ width: 22, height: 22, flexShrink: 0 }}
                />
                <span>
                  Simulando fretes
                  {selectedCount > 0
                    ? ` para ${selectedCount} embarque(s)`
                    : ` para todos os ${embarques.length} embarque(s)`}... aguarde.
                </span>
              </div>
            )}

            {aplicandoSelecao && (
              <div className="cotacao-lote-progress-panel">
                <div
                  className="spinner-border text-info"
                  role="status"
                  style={{ width: 22, height: 22, flexShrink: 0 }}
                />
                <span>
                  Salvando a seleção da transportadora mais barata no banco... <strong>não saia da tela</strong>.
                </span>
              </div>
            )}

            {loadingEmbarques && (
              <div className="cotacao-lote-progress-panel">
                <div
                  className="spinner-border text-info"
                  role="status"
                  style={{ width: 22, height: 22, flexShrink: 0 }}
                />
                <span>Buscando embarques pendentes dos últimos {pendentesDays} dias...</span>
              </div>
            )}

            <div className="cotacao-lote-table-panel custom-table-shell--themed-pagination">
              {importing ? (
                <div className="cotacao-lote-empty">
                  <div
                    className="spinner-border text-info"
                    role="status"
                    style={{ width: 40, height: 40 }}
                  />
                  <p>Carregando embarques importados...</p>
                </div>
              ) : embarques.length === 0 ? (
                <div className="cotacao-lote-empty">
                  <i className="tim-icons icon-cloud-upload-94" />
                  <p>Nenhum embarque importado. Clique em "Importar embarques" para começar.</p>
                </div>
              ) : (
                <BootstrapTable
                  keyField="_id"
                  data={filteredEmbarques}
                  columns={columns}
                  selectRow={selectRow}
                  rowEvents={rowEvents}
                  pagination={paginationFactory(paginationOptions)}
                  bootstrap4
                  bordered={false}
                  hover
                  noDataIndication="Nenhum embarque encontrado com os filtros aplicados."
                />
              )}
            </div>
          </SectionShell>
        </div>

        {isPanelOpen && activeEmbarque && (
          <ProposalPanel
            embarque={activeEmbarque}
            proposals={simulations[activeEmbarque._id] || []}
            indicador={indicadores[activeEmbarque._id]}
            onClose={() => setIsPanelOpen(false)}
            onApply={handleApplyTransportadora}
            onOpenFullQuote={handleOpenFullQuote}
            idEmbarcador={empresaId}
            onAddSpot={handleAddSpot}
            onRemoveSpot={handleRemoveSpot}
          />
        )}
      </div>

      <Modal
        isOpen={isImportModalOpen}
        toggle={() => setIsImportModalOpen(false)}
        className="simulation-modal"
        backdropClassName="simulation-modal-backdrop"
        wrapClassName="simulation-modal-wrapper"
      >
        <ModalHeader
          toggle={() => setIsImportModalOpen(false)}
          className="simulation-modal"
        >
          Importar embarques por planilha
        </ModalHeader>
        <ModalBody>
          {empresaId ? (
            <>
              <p style={{ color: "var(--everlog-card-muted)", fontSize: "0.82rem", marginBottom: 12 }}>
                Colunas obrigatórias: <strong>Numero</strong>, <strong>CnpjRemetente</strong>,{" "}
                <strong>CepDestino</strong>, <strong>ValorNF</strong> e <strong>Peso</strong>. Informe{" "}
                <strong>Volume</strong> em m³ para que a cotação considere o peso cubado — sem ele, vale o peso
                real.{" "}
                <a
                  href="#modelo"
                  onClick={(e) => {
                    e.preventDefault();
                    baixarModelo("xlsx");
                  }}
                >
                  Baixar modelo (.xlsx)
                </a>{" "}
                <span style={{ opacity: 0.7 }}>
                  ou{" "}
                  <a
                    href="#modelo-csv"
                    onClick={(e) => {
                      e.preventDefault();
                      baixarModelo("csv");
                    }}
                  >
                    em CSV
                  </a>
                </span>
              </p>
              <FileUpload
                api={api}
                url={urlPostImportCsv}
                /* `empresaId` é um Embarcador.Id. Mandar isso num parâmetro
                   chamado `idEmbarcadorRemetente` fazia o escopoParametros
                   (PO1-3102) resolver o id como EmbarcadorRemetente e negar
                   a importação com "Fora do seu escopo de acesso". */
                params={{ idEmbarcador: empresaId }}
                accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,application/csv,text/plain"
                filekey="file"
                onSuccess={handleImportSuccess}
              />
            </>
          ) : (
            <p style={{ color: "var(--everlog-card-muted)", textAlign: "center" }}>
              Selecione um embarcador para importar embarques.
            </p>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default CotacaoEmLote;
